import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle2, Clock, Info, Paperclip, Trash2, PhoneCall, MapPin, Mail, Circle } from "lucide-react";
import { LeaveReviewButton } from "@/components/ReviewsSection";
import { Logo } from "@/components/Logo";
import { api, apiError } from "@/lib/api";
import { trackSchedule } from "@/lib/pixels";
import { DocThumb, DocViewer } from "@/components/DocPreview";

// Seul document demandé : le relevé de résultat du code (si l'élève a déjà le code).
const CATS_HAS_CODE = [
  { code: "code_result", label: "Relevé de résultat du code de la route" },
];

export default function ReservationSuivi() {
  const { rid } = useParams();
  const location = useLocation();
  const appliedRef = useRef(false);
  const [resa, setResa] = useState(null);
  const [error, setError] = useState("");
  const [neph, setNeph] = useState({ has_neph: null, neph_number: "" });
  const [savingNeph, setSavingNeph] = useState(false);
  const [uploadingCat, setUploadingCat] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [viewDoc, setViewDoc] = useState(null);
  const closeViewer = useCallback(() => setViewDoc(null), []);

  const load = useCallback(() => {
    api.get(`/public/booking/reservation/${rid}`)
      .then(({ data }) => { setResa(data); setNeph({ has_neph: data.has_neph, neph_number: data.neph_number || "" }); })
      .catch((e) => setError(apiError(e.response?.data?.detail) || e.message));
  }, [rid]);

  useEffect(() => { load(); }, [load]);

  // Applique automatiquement la réponse NEPH transmise depuis le formulaire de réservation.
  useEffect(() => {
    const st = location.state;
    if (!resa || appliedRef.current) return;
    if (st && (st.has_neph === true || st.has_neph === false) && (resa.has_neph === null || resa.has_neph === undefined)) {
      appliedRef.current = true;
      api.post(`/public/booking/reservation/${rid}/neph`, { has_neph: st.has_neph, neph_number: (st.neph_number || "").trim() })
        .then(({ data }) => { setResa(data); setNeph({ has_neph: data.has_neph, neph_number: data.neph_number || "" }); })
        .catch(() => {});
    }
  }, [resa, location.state, rid]);

  const saveNeph = async (value) => {
    setSavingNeph(true);
    try {
      const { data } = await api.post(`/public/booking/reservation/${rid}/neph`, {
        has_neph: value, neph_number: value ? neph.neph_number.trim() : "",
      });
      setResa(data);
      toast.success("Enregistré.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingNeph(false); }
  };

  const uploadCat = (code) => async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const perCat = resa.doc_limits?.per_category ?? 3;
    const maxTotal = resa.doc_limits?.total ?? 10;
    const already = (resa.documents || []).filter((d) => d.category === code).length;
    if (already + files.length > perCat) {
      toast.error(`Maximum ${perCat} fichiers pour ce document. Supprime un fichier pour le remplacer.`);
      e.target.value = "";
      return;
    }
    if ((resa.documents || []).length + files.length > maxTotal) {
      toast.error(`Maximum ${maxTotal} fichiers par dossier. Supprime un fichier pour le remplacer.`);
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("category", code);
    files.forEach((f) => fd.append("files", f));
    setUploadingCat(code);
    try {
      const { data } = await api.post(`/public/booking/reservation/${rid}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResa(data);
      toast.success("Document ajouté.");
      // Fin de réservation (dossier complet) -> événement Meta « Schedule » (1 seule fois)
      if (data?.docs_complete) {
        trackSchedule({ reservationId: rid, formula: data.offer_title, city: data.city });
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploadingCat(""); e.target.value = ""; }
  };

  const deleteDoc = async (doc, { ask = true } = {}) => {
    if (ask && !window.confirm(`Supprimer « ${doc.filename} » ?`)) return;
    setDeletingId(doc.id);
    try {
      const { data } = await api.delete(`/public/booking/reservation/${rid}/documents/${doc.id}`);
      setResa(data);
      setViewDoc(null);
      toast.success("Document supprimé.");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setDeletingId(""); }
  };

  if (error) return (
    <div className="min-h-screen bg-[#FAFAFA] px-6 py-20"><div className="mx-auto max-w-lg rounded-2xl border border-neutral-200 bg-white p-8"><Logo className="h-12" /><p className="mt-6 text-red-600" data-testid="resa-error">{error}</p><Link to="/reserver" className="mt-4 inline-block font-semibold text-brand">Faire une nouvelle réservation</Link></div></div>
  );
  if (!resa) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  const released = resa.status === "released" || resa.status === "cancelled";
  const done = resa.status === "confirmed" || resa.status === "enrolled";
  const cats = resa.has_code === true ? CATS_HAS_CODE : [];
  const docsByCat = (code) => (resa.documents || []).filter((d) => d.category === code);
  const perCat = resa.doc_limits?.per_category ?? 3;
  const maxTotal = resa.doc_limits?.total ?? 10;
  const totalDocs = (resa.documents || []).length;
  const st = resa.status;
  const transmitted = ["transmitted", "confirmed", "enrolled"].includes(st);
  const needCode = resa.has_code === true && !resa.docs_complete;
  // Frise des étapes du dossier
  const timeline = [
    { key: "reserved", label: "Place réservée", done: true },
    { key: "docs", label: resa.has_code ? "Relevé du code ajouté" : "Dossier complet", done: !!resa.docs_complete },
    { key: "transmitted", label: "Dossier transmis à ton auto-école", done: transmitted },
    { key: "confirmed", label: "Inscription confirmée par e-mail (OUI)", done: st === "confirmed" || st === "enrolled" },
    { key: "enrolled", label: "Inscription finalisée à l'auto-école", done: st === "enrolled" },
  ];
  const currentIdx = timeline.findIndex((t) => !t.done);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="border-b border-neutral-200 bg-white"><div className="mx-auto max-w-2xl px-5 py-3"><Link to="/"><Logo className="h-10" /></Link></div></header>
      <main className="mx-auto max-w-2xl px-5 py-8">
        <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="resa-summary">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Place réservée</span>
          <h1 className="mt-3 text-2xl font-black text-neutral-900">Bonjour {resa.first_name}, ta place est bloquée</h1>
          <p className="mt-1 text-sm text-neutral-600"><strong>{resa.offer_title}</strong> — {resa.city} · {resa.month_label}{resa.forfait_price != null ? ` · ${resa.forfait_price} €` : ""}</p>

          <div className="mt-4 flex items-start gap-2 rounded-xl bg-brand/5 p-4 text-sm text-neutral-700" data-testid="resa-status-msg">
            <Clock className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
            {done ? (
              <span>{"Ton inscription est confirmée. Félicitations et bonne route !"}</span>
            ) : released ? (
              <span>Cette place a été libérée. Tu peux <Link to="/reserver" className="font-semibold text-brand">réserver à nouveau</Link> si des places sont disponibles.</span>
            ) : needCode ? (
              <span><strong>{"À faire maintenant : "}</strong>{"ajoute ton relevé de résultat du code plus bas, puis appelle ton auto-école."}</span>
            ) : (
              <span><strong>{"À faire maintenant : "}</strong>{"appelle ton auto-école "}<strong>sous 2 jours</strong>{" pour fixer ton rendez-vous d'inscription. C'est toi qui fais le premier pas."}</span>
            )}
          </div>
        </div>

        {/* Ton auto-école + gros bouton d'appel */}
        {!released && resa.school_name && (
          <div className="mt-5 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="resa-school">
            <h2 className="text-lg font-bold text-neutral-900">Ton auto-école</h2>
            <div className="mt-3 rounded-xl border border-neutral-200 bg-neutral-50/70 p-4 text-sm text-neutral-700">
              <p className="font-bold text-neutral-900" data-testid="resa-school-name">{resa.school_name}</p>
              {resa.school_address && <p className="mt-1 flex items-start gap-1.5"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" /> {resa.school_address}</p>}
              {resa.school_phone && <p className="mt-1 flex items-center gap-1.5"><PhoneCall className="h-4 w-4 shrink-0 text-neutral-400" /> {resa.school_phone}</p>}
            </div>
            {!done && resa.school_phone && (
              <a href={`tel:${resa.school_phone.replace(/\s/g, "")}`} data-testid="resa-call-school"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-brand px-6 py-4 text-base font-bold text-white shadow-lg shadow-brand/20 hover:bg-brand-hover">
                <PhoneCall className="h-5 w-5" /> {"Appeler l'auto-école"}
              </a>
            )}
            {!done && (
              <p className="mt-3 text-sm text-neutral-700">
                <strong>{"Appelle-la dès que possible (sous 2 jours)"}</strong>{resa.has_code ? " et lui transmettre ton relevé de résultat du code." : "."}
              </p>
            )}
            <p className="mt-3 rounded-xl bg-brand/5 px-4 py-3 text-sm leading-relaxed text-neutral-700" data-testid="resa-fair-note">
              {"💙 Ton auto-école n'a parfois qu'une seule place à offrir ce mois-ci : en l'appelant vite, tu sécurises ta place. Si tu changes d'avis, préviens-la pour qu'un autre élève puisse en profiter."}
            </p>
          </div>
        )}

        {/* Frise : où en est mon dossier ? */}
        {!released && (
          <div className="mt-5 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="resa-timeline">
            <h2 className="text-lg font-bold text-neutral-900">Où en est mon dossier ?</h2>
            <ol className="mt-4 space-y-0">
              {timeline.map((t, i) => {
                const current = i === currentIdx;
                return (
                  <li key={t.key} className="relative flex gap-3 pb-4 last:pb-0" data-testid={`timeline-${t.key}`} data-done={t.done ? "true" : "false"}>
                    {i < timeline.length - 1 && <span className={`absolute left-[11px] top-6 h-[calc(100%-1.25rem)] w-0.5 ${t.done ? "bg-emerald-300" : "bg-neutral-200"}`} />}
                    {t.done ? <CheckCircle2 className="relative h-6 w-6 shrink-0 text-emerald-500" />
                      : current ? <span className="relative grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 border-brand bg-white"><span className="h-2 w-2 rounded-full bg-brand" /></span>
                        : <Circle className="relative h-6 w-6 shrink-0 text-neutral-300" />}
                    <span className={`pt-0.5 text-sm ${t.done ? "font-semibold text-neutral-800" : current ? "font-bold text-brand" : "text-neutral-400"}`}>
                      {t.label}{current ? " — en cours" : ""}
                    </span>
                  </li>
                );
              })}
            </ol>
            <p className="mt-4 flex items-start gap-2 text-xs text-neutral-500"><Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {"On te prévient par e-mail à chaque étape. Pense à vérifier tes spams."}</p>
          </div>
        )}

        {done && (
          <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="resa-done-review">
            <p className="font-bold text-emerald-900">{"Ça s'est bien passé ?"}</p>
            <p className="mt-1 text-sm text-emerald-800">{"Ton avis aide d'autres élèves à trouver leur place."}</p>
            <LeaveReviewButton label="Laisser un avis" testId="resa-review-btn" className="mt-3 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-700" />
          </div>
        )}

        {!released && !done && (
          <>
            {/* NEPH */}
            <div className="mt-5 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="resa-neph">
              <h2 className="flex items-center gap-2 text-lg font-bold text-neutral-900"><Info className="h-5 w-5 text-brand" /> Numéro NEPH</h2>
              <p className="mt-1 text-sm text-neutral-500">{"Le NEPH est ton numéro d'enregistrement (12 chiffres). Si tu n'en as pas encore, ce n'est pas grave : l'auto-école s'en occupera avec toi."}</p>
              <div className="mt-3 flex gap-2">
                {[["J'ai un NEPH", true], ["Je n'en ai pas", false]].map(([lbl, val]) => (
                  <button key={lbl} type="button" onClick={() => setNeph((n) => ({ ...n, has_neph: val }))}
                    className={`rounded-xl border px-4 py-2 text-sm font-bold ${neph.has_neph === val ? "border-brand bg-brand/10 text-brand" : "border-neutral-300 text-neutral-600"}`}>{lbl}</button>
                ))}
              </div>
              {neph.has_neph === true && (
                <input value={neph.neph_number} onChange={(e) => setNeph((n) => ({ ...n, neph_number: e.target.value }))}
                  placeholder="Ex. 051227308989" data-testid="neph-input"
                  className="mt-3 w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-brand" />
              )}
              <button type="button" disabled={neph.has_neph === null || savingNeph} onClick={() => saveNeph(neph.has_neph)} data-testid="neph-save"
                className="mt-3 inline-flex items-center gap-2 rounded-xl bg-neutral-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-neutral-800 disabled:opacity-50">
                {savingNeph ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Enregistrer
              </button>
            </div>

            {/* Documents : uniquement le relevé du code si l'élève a le code */}
            {cats.length > 0 ? (
            <div className="mt-5 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="resa-docs">
              <h2 className="flex items-center gap-2 text-lg font-bold text-neutral-900"><Paperclip className="h-5 w-5 text-brand" /> Mes documents</h2>
              <p className="mt-1 text-sm text-neutral-500">{`Formats acceptés : JPEG, PNG, PDF (15 Mo max). ${perCat} fichiers max par document, ${maxTotal} au total.`}</p>
              <div className="mt-4 space-y-3">
                {cats.map((c) => {
                  const got = docsByCat(c.code);
                  const full = got.length >= perCat || totalDocs >= maxTotal;
                  return (
                    <div key={c.code} className="rounded-xl border border-neutral-200 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-semibold text-neutral-800">{c.label}</span>
                        {got.length > 0 && <span className="inline-flex shrink-0 items-center gap-1 text-xs font-bold text-emerald-600" data-testid={`count-${c.code}`}><CheckCircle2 className="h-4 w-4" /> {got.length}/{perCat}</span>}
                      </div>
                      {got.length > 0 && (
                        <>
                          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
                            {got.map((d) => (
                              <div key={d.id} className="relative" data-testid={`doc-${d.id}`}>
                                <DocThumb rid={rid} doc={d} onOpen={setViewDoc} />
                                <button type="button" onClick={() => deleteDoc(d)} disabled={deletingId === d.id} data-testid={`delete-doc-${d.id}`}
                                  className="absolute right-1 top-1 grid h-7 w-7 place-items-center rounded-full bg-white/95 text-red-600 shadow hover:bg-red-50 disabled:opacity-50" aria-label={`Supprimer ${d.filename}`}>
                                  {deletingId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                                </button>
                              </div>
                            ))}
                          </div>
                          <p className="mt-1.5 text-[11px] text-neutral-400">Touche une miniature pour vérifier ton document.</p>
                        </>
                      )}
                      {full ? (
                        <p className="mt-3 text-xs text-neutral-500" data-testid={`limit-${c.code}`}>
                          {got.length >= perCat ? `Limite de ${perCat} fichiers atteinte` : `Limite de ${maxTotal} fichiers par dossier atteinte`} : supprime un fichier pour le remplacer.
                        </p>
                      ) : (
                        <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-brand/10 px-4 py-2 text-sm font-bold text-brand hover:bg-brand/20" data-testid={`upload-${c.code}`}>
                          {uploadingCat === c.code ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Ajouter
                          <input type="file" multiple accept="image/*,application/pdf" onChange={uploadCat(c.code)} className="hidden" />
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="mt-4 text-center text-xs text-neutral-400">{"Dès ton dossier complet, notre équipe le transmet à l'auto-école et tu reçois un email de confirmation."}</p>
            </div>
            ) : (
              <div className="mt-5 flex items-start gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800" data-testid="resa-no-docs">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                <span>{"Aucun document à fournir. Notre équipe transmet ton dossier à l'auto-école et tu reçois un email de confirmation."}</span>
              </div>
            )}
          </>
        )}
      </main>
      <DocViewer rid={rid} doc={viewDoc} onClose={closeViewer}
        onDelete={(d) => deleteDoc(d, { ask: false })} deleting={!!viewDoc && deletingId === viewDoc.id} />
    </div>
  );
}
